'use client';

import React, { useContext, useMemo } from 'react';
import type { Bounds } from 'hooks/useFlip/types';
import { noop } from 'utils/noop';

type SharedHoverContextType = {
  onEnterTarget: (bounds: Bounds) => void;
  onLeaveTarget: () => void;
  onLockTarget: (bounds: Bounds) => void;
  onUnlockTarget: (bounds: Bounds) => void;
};

const defaultFunctions = {
  onEnterTarget: noop,
  onLeaveTarget: noop,
  onLockTarget: noop,
  onUnlockTarget: noop,
};

export const createSharedHoverContext = () => {
  const SharedHoverContext =
    React.createContext<SharedHoverContextType>(undefined);

  const useSharedHoverContext = () => {
    const bind = useContext(SharedHoverContext);
    const fns = bind ?? defaultFunctions;

    return useMemo(() => {
      return {
        enabled: !!bind,
        ...fns,
      };
    }, [bind, fns]);
  };

  const SharedHoverProvider = React.memo(
    ({
      children,
      value,
    }: {
      children: React.ReactNode;
      value: SharedHoverContextType;
    }) => {
      return (
        <SharedHoverContext.Provider value={value}>
          {children}
        </SharedHoverContext.Provider>
      );
    }
  );

  return {
    SharedHoverContext,
    SharedHoverProvider,
    useSharedHoverContext,
  };
};
